// Field Time Report preview + share modal.
//
// Shows the coach exactly what the parent will receive (the real page
// components, scaled to fit the screen), then captures full-size off-screen
// copies into a letter PDF and opens the share sheet. Preview and PDF render
// from the same components, so they can never differ.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Plus, Share2 } from 'lucide-react';
import { FieldTimeReportData } from '../../lib/field-time-report';
import { shareFieldTimeReport } from '../../lib/field-time-pdf';
import { tapLight } from '../../lib/haptics';
import {
  ReportSummaryPage,
  ReportGameLogPage,
  GAME_LOG_ROWS_PER_PAGE,
} from './FieldTimeReportPages';

const PAGE_W = 816;
const PAGE_H = 1056;

export const FieldTimeReportModal = ({
  data,
  onClose,
}: {
  data: FieldTimeReportData;
  onClose: () => void;
}) => {
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState(false);
  const [scale, setScale] = useState(() => Math.min(1, (window.innerWidth - 40) / PAGE_W));
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const onResize = () => setScale(Math.min(1, (window.innerWidth - 40) / PAGE_W));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const logChunks = useMemo(() => {
    const chunks: FieldTimeReportData['gameLog'][] = [];
    for (let i = 0; i < data.gameLog.length; i += GAME_LOG_ROWS_PER_PAGE) {
      chunks.push(data.gameLog.slice(i, i + GAME_LOG_ROWS_PER_PAGE));
    }
    return chunks;
  }, [data.gameLog]);

  const totalPages = 1 + logChunks.length;
  const hasData = data.gamesTracked > 0;

  const renderPages = (refs?: React.MutableRefObject<(HTMLDivElement | null)[]>) => (
    <>
      <ReportSummaryPage
        ref={refs ? el => { refs.current[0] = el; } : undefined}
        data={data}
        totalPages={totalPages}
      />
      {logChunks.map((rows, i) => (
        <ReportGameLogPage
          key={i}
          ref={refs ? el => { refs.current[i + 1] = el; } : undefined}
          data={data}
          rows={rows}
          page={i + 2}
          totalPages={totalPages}
        />
      ))}
    </>
  );

  const handleShare = async () => {
    if (sharing || !hasData) return;
    tapLight();
    setShareError(false);
    setSharing(true);
    try {
      const nodes = pageRefs.current.filter((n): n is HTMLDivElement => !!n);
      await shareFieldTimeReport(nodes, data);
    } catch {
      setShareError(true);
    } finally {
      setSharing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[310] bg-raised flex flex-col safe-top"
    >
      {/* Header */}
      <div className="flex-none px-6 pt-6 pb-3 flex items-center justify-between bg-surface border-b border-hairline">
        <div>
          <h2 className="text-lg font-black italic tracking-tight uppercase text-ink">Field Time Report</h2>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-2">
            {data.playerNumber ? `#${data.playerNumber} ` : ''}{data.playerName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasData && (
            <button
              onClick={handleShare}
              disabled={sharing}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-surface font-black text-[11px] uppercase italic tracking-widest disabled:opacity-50"
            >
              <Share2 className="w-4 h-4" />
              <span>{sharing ? 'Preparing…' : 'Share'}</span>
            </button>
          )}
          <button onClick={onClose} className="p-2 text-ink-2 hover:text-ink">
            <Plus className="w-6 h-6 rotate-45" />
          </button>
        </div>
      </div>

      {shareError && (
        <div className="flex-none px-6 py-2 bg-[#FEF2F2] border-b border-[#FECACA]">
          <p className="text-[11px] font-bold text-[#B91C1C]">
            Couldn't create the PDF. Please try again.
          </p>
        </div>
      )}

      {/* Body */}
      {hasData ? (
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="flex flex-col items-center gap-5 pb-10">
            {[0, ...logChunks.map((_, i) => i + 1)].map(pageIdx => (
              <div
                key={pageIdx}
                style={{ width: PAGE_W * scale, height: PAGE_H * scale }}
                className="rounded-lg shadow-lg overflow-hidden"
              >
                <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                  {pageIdx === 0 ? (
                    <ReportSummaryPage data={data} totalPages={totalPages} />
                  ) : (
                    <ReportGameLogPage
                      data={data}
                      rows={logChunks[pageIdx - 1]}
                      page={pageIdx + 1}
                      totalPages={totalPages}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-10 text-center">
          <p className="font-black italic uppercase text-ink mb-2">No field time recorded yet</p>
          <p className="text-sm text-ink-2">
            Field time is captured automatically when you run live games in Dugout or import
            GameChanger box scores. Once a game has position data, this report builds itself.
          </p>
        </div>
      )}

      {/* Off-screen full-size pages for PDF capture */}
      {hasData && (
        <div className="fixed -left-[9999px] top-0" aria-hidden>
          {renderPages(pageRefs)}
        </div>
      )}
    </motion.div>
  );
};
